import { useState, useCallback, useEffect } from 'react';
import { showConfirm } from '@/utils/common/alert';
import { roleNamesAndDescriptions } from '@/types/admin/userManagement';
import { IUserManagementTableRow } from '@/types/admin/userManagement';
import { 
  validateData, 
  composeValidators, 
  requiredString, 
  emailFormat, 
  requireArrayLengthIf 
} from '@/utils/admin/validation';

export interface EditUserFormData {
  email: string;
  firstName: string;
  lastName: string;
  roleName: string;
  organizations?: string[];
  properties?: string[];
}

export const useEditUserForm = (user: IUserManagementTableRow) => {
  // Initialize form data with existing user data
  const [formData, setFormData] = useState<EditUserFormData>({
    email: user.email,
    firstName: user.name.split(' ')[0] || '',
    lastName: user.name.split(' ').slice(1).join(' ') || '',
    roleName: user.userType || '',
    organizations: (user.organization || []).filter((org): org is string => org !== null),
    properties: (user.properties || []).filter((prop): prop is string => prop !== null),
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [activeStep, setActiveStep] = useState(0);

  // Sync when user changes (desktop side panel passes new user on row click)
  useEffect(() => {
    setFormData({
      email: user.email,
      firstName: user.name.split(' ')[0] || '',
      lastName: user.name.split(' ').slice(1).join(' ') || '',
      roleName: user.userType || '',
      organizations: (user.organization || []).filter((org): org is string => org !== null),
      properties: (user.properties || []).filter((prop): prop is string => prop !== null),
    });
    setErrors({});
    setActiveStep(0);
  }, [user]);

  // Form field change handler
  const handleFieldChange = useCallback((field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  }, [errors]);

  // Step-specific validators
  const accountInfoValidator = composeValidators<EditUserFormData>(
    requiredString('firstName', 'First name'),
    requiredString('lastName', 'Last name'),
    requiredString('email', 'Email'),
    emailFormat('email')
  );

  const roleSelectionValidator = composeValidators<EditUserFormData>(
    requiredString('roleName', 'User role'),
    requireArrayLengthIf(
      (data) => data.roleName === roleNamesAndDescriptions.orgAdmin.value,
      'organizations',
      'organization'
    ),
    requireArrayLengthIf(
      (data) => [
        roleNamesAndDescriptions.propertyAdmin.value,
        roleNamesAndDescriptions.billingAdmin.value,
      ].includes(data.roleName),
      'properties',
      'property'
    )
  );

  // Form validation
  const validateCurrentStep = useCallback(() => {
    if (activeStep === 0) {
      return validateData(formData, setErrors, accountInfoValidator);
    } else if (activeStep === 1) {
      return validateData(formData, setErrors, roleSelectionValidator);
    }
    return true;
  }, [activeStep, formData]);

  // Step navigation
  const handleNext = useCallback(() => {
    if (validateCurrentStep()) {
      setActiveStep(prev => prev + 1);
    }
  }, [validateCurrentStep]);

  const handleBack = useCallback(() => {
    setActiveStep(prev => Math.max(0, prev - 1));
  }, []);

  // Jump to a specific step (used by desktop tabs)
  const goToStep = useCallback((index: number) => {
    const clamped = Math.max(0, Math.min(2, index));
    setActiveStep(clamped);
  }, []);

  // Complete form validator (combines all steps)
  const completeFormValidator = composeValidators<EditUserFormData>(
    // Account info validation
    requiredString('firstName', 'First name'),
    requiredString('lastName', 'Last name'),
    requiredString('email', 'Email'),
    emailFormat('email'),
    // Role selection validation
    requiredString('roleName', 'User role'),
    requireArrayLengthIf(
      (data) => data.roleName === roleNamesAndDescriptions.orgAdmin.value,
      'organizations',
      'organization'
    ),
    requireArrayLengthIf(
      (data) => [
        roleNamesAndDescriptions.propertyAdmin.value,
        roleNamesAndDescriptions.billingAdmin.value,
      ].includes(data.roleName),
      'properties',
      'property'
    )
  );

  // Final form validation
  const validateForm = useCallback(() => {
    return validateData(formData, setErrors, completeFormValidator);
  }, [formData]);

  // Discard alert
  const showDiscardAlert = useCallback(async (onConfirm: () => void) => {
    // Check if any data has changed from original
    const hasChanges = 
      formData.firstName !== (user.name.split(' ')[0] || '') ||
      formData.lastName !== (user.name.split(' ').slice(1).join(' ') || '') ||
      formData.email !== user.email ||
      formData.roleName !== (user.userType || '') ||
      JSON.stringify(formData.organizations) !== JSON.stringify((user.organization || []).filter((org): org is string => org !== null)) ||
      JSON.stringify(formData.properties) !== JSON.stringify((user.properties || []).filter((prop): prop is string => prop !== null));

    if (hasChanges) {
      const confirmed = await showConfirm(
        'Discard Changes',
        'Are you sure you want to discard your changes?',
        'Discard',
        'Keep Editing'
      );
      
      if (confirmed) {
        onConfirm();
      }
    } else {
      onConfirm();
    }
  }, [formData, user]);

  // Cleanup on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      setFormData({
        email: '',
        firstName: '',
        lastName: '',
        roleName: '',
        organizations: [],
        properties: [],
      });
      setErrors({});
      setActiveStep(0);
    };
  }, []);

  // Back to the user's saved values; the desktop side panel stays mounted across opens.
  const resetForm = useCallback(() => {
    setFormData({
      email: user.email,
      firstName: user.name.split(' ')[0] || '',
      lastName: user.name.split(' ').slice(1).join(' ') || '',
      roleName: user.userType || '',
      organizations: (user.organization || []).filter((org): org is string => org !== null),
      properties: (user.properties || []).filter((prop): prop is string => prop !== null),
    });
    setErrors({});
    setActiveStep(0);
  }, [user]);

  return {
    formData,
    errors,
    activeStep,
    handleFieldChange,
    handleNext,
    handleBack,
    goToStep,
    validateCurrentStep,
    validateForm,
    showDiscardAlert,
    resetForm,
  };
};
